import { ArrowRightIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useStartPhoneNumberVerification } from 'components/hooks/member_onboarding_v2/useStartPhoneNumberVerification'
import { useVerifyPhoneNumber } from 'components/hooks/member_onboarding_v2/useVerifyPhoneNumber'
import { FormTextFieldDS } from 'components/reusable/form/FormFields'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'

type PhoneVerificationProps = {
  phone_verification_code: string;
}

export function PhoneVerificationPage ({
  navigateToNextStep,
  navigateToPreviousStep,
}: StepComponentProps) {
  const { onboardingData } = useOnboarding()
  const [submitError, setSubmitError] = useState<React.ReactElement | null>(
    null
  )
  const { breakpointIsAtLeast } = useBreakpoint()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<PhoneVerificationProps>({
    defaultValues: {
      phone_verification_code: '',
    },
    mode: 'onChange',
  })

  const {
    mutate: startPhoneNumberVerification,
    isPending: isStartingPhoneNumberVerification,
  } = useStartPhoneNumberVerification()
  const { mutate: verifyPhoneNumber, isPending: isVerifyingPhoneNumber } =
    useVerifyPhoneNumber()

  useEffect(() => {
    startPhoneNumberVerification()
  }, [])

  const onSubmit = (formData: PhoneVerificationProps) => {
    verifyPhoneNumber(formData, {
      onSuccess: () => {
        navigateToNextStep()
      },
      onError: (error) => {
        if (error.status === 422) {
          const fieldErrors = error.fieldErrors!
          Object.entries(fieldErrors).forEach(([field, errorMessages]) => {
            setError(field as keyof PhoneVerificationProps, {
              type: 'manual',
              message: errorMessages.join(', '),
            })
          })
        } else {
          const errorMessage = <OnboardingGenericErrorMessage error={error} />
          setSubmitError(errorMessage)
        }
      },
    })
  }

  function renderButtons () {
    const backButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
        disabled={isStartingPhoneNumberVerification || isVerifyingPhoneNumber}
      >
        Back
      </Button>
    )

    const nextButton = (
      <Button
        rightIcon={ArrowRightIcon}
        variant='primary'
        type='submit'
        disabled={
          isStartingPhoneNumberVerification ||
          isVerifyingPhoneNumber ||
          !!submitError ||
          !isValid
        }
      >
        Next
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {backButton}
          {nextButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {nextButton}
        {backButton}
      </ButtonGroup>
    )
  }

  return (
    <div data-testid='PhoneVerificationPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Verify SMS code
        </h1>
        <p className='body text-gray-500'>
          Type in the 6-digit code we just sent to the phone number ending in{' '}
          {onboardingData?.member?.phone_number?.slice(-4)}.
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-10'>
          <FormTextFieldDS<PhoneVerificationProps>
            error={errors.phone_verification_code}
            fieldId='phone_verification_code'
            fieldLabel='Code'
            register={register}
            placeholder='xxxxxx'
            disabled={
              isStartingPhoneNumberVerification || isVerifyingPhoneNumber
            }
            validationRule={{
              required: 'Verification code is required.',
              validate: (value: string) =>
                value.length === 6 || 'Verification code must be 6 digits.'
            }}
          />
        </div>
        {submitError && (
          <div className='mb-10'>
            <Banner
              title='A problem has occurred while processing your request'
              type='critical'
            >
              {submitError}
            </Banner>
          </div>
        )}
        {renderButtons()}
      </form>
    </div>
  )
}
